import { useEffect, useMemo } from 'react';
import { useGetPayoutReviewQuery } from '../../../store/api/payoutsApi';
import CertificateArt from './CertificateArt';
import { downloadRenderedCertificate } from './certificateExport';
import Skeleton from '../../../reusable/Skeleton/Skeleton';
import { toast } from '../../../reusable/Toast/toastStore';
import { signedUsd, usd } from '../../../utilities/format';
import { shareCertificateLink } from './ShareCertificateDialog';
import { t } from '../../../i18n/i18n';
import './PayoutReviewModal.scss';

const SAMPLE_REVIEW = {
  period: {
    from: '2026-02-19T11:42:01.446948Z',
    to: '2026-02-24T05:43:54.100343Z',
  },
  lifetime_payouts: 1446,
  trades: 15,
  wins: 2,
  losses: 12,
  breakeven: 1,
  win_rate: 13,
  long_short: {
    long_trades: 6,
    short_trades: 9,
    long_percent: 40,
    short_percent: 60,
  },
  per_asset: [
    { asset: 'EURUSD.pro', volume: 1.99, trades: 1, win_rate: 100, net_pnl: 7.96 },
    { asset: 'CADJPY.pro', volume: 1.99, trades: 1, win_rate: 0, net_pnl: 0 },
    { asset: 'EURAUD.pro', volume: 1, trades: 1, win_rate: 0, net_pnl: -16.91 },
    { asset: 'AUDCAD.pro', volume: 28.49, trades: 12, win_rate: 8, net_pnl: -274.16 },
  ],
  best_trade: {
    symbol: 'AUDCAD.pro',
    side: 'Sell',
    volume: 2,
    open_price: 0.96451,
    close_price: 0.96442,
    net_profit: 13.15,
    open_time: '2026-02-19T14:46:19',
    close_time: '2026-02-19T14:47:37',
  },
  worst_trade: {
    symbol: 'AUDCAD.pro',
    side: 'Buy',
    volume: 3,
    open_price: 0.96436,
    close_price: 0.96414,
    net_profit: -48.22,
    open_time: '2026-02-19T14:48:11',
    close_time: '2026-02-19T14:48:24',
  },
  pnl_distribution: [
    { from: -48.22, to: -42.08, count: 2, type: 'loss' },
    { from: -42.08, to: -35.95, count: 0, type: 'loss' },
    { from: -35.95, to: -29.81, count: 4, type: 'loss' },
    { from: -29.81, to: -23.67, count: 1, type: 'loss' },
    { from: -23.67, to: -17.54, count: 0, type: 'loss' },
    { from: -17.54, to: -11.4, count: 2, type: 'loss' },
    { from: -11.4, to: -5.26, count: 2, type: 'loss' },
    { from: -5.26, to: 0.88, count: 2, type: 'win' },
    { from: 0.88, to: 7.01, count: 0, type: 'win' },
    { from: 7.01, to: 13.15, count: 2, type: 'win' },
  ],
  certificate_id: 411950,
};

const formatDateRange = (fromStr, toStr) => {
  if (!fromStr || !toStr) return '';
  const from = new Date(fromStr);
  const to = new Date(toStr);
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return '';
  const f = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  return `${f.format(from)} → ${f.format(to)}`;
};

const formatVolume = (vol) => {
  if (vol == null) return '—';
  const n = Number(vol);
  if (!Number.isFinite(n)) return String(vol);
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const cleanAsset = (name) => (name ? String(name).replace(/\.pro$/i, '') : '—');

export default function PayoutReviewModal({ open, item, onClose, onShare }) {
  const certificateId = item?.id ?? item?.certificate_id;

  const handleShareClick = () => {
    if (onShare) {
      onShare(item);
    } else if (item?.id) {
      const link = shareCertificateLink(item.id);
      if (link && navigator.clipboard) {
        navigator.clipboard.writeText(link);
        toast.success(t('Link copied'), t('Certificate link copied to clipboard.'));
      }
    }
  };

  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetPayoutReviewQuery(
    { certificate_id: certificateId },
    { skip: !open || !certificateId }
  );

  const loading = isLoading || isFetching;

  // Use API response when available, fallback to provided sample review data
  const review = useMemo(() => {
    if (!item) return SAMPLE_REVIEW;
    if (apiData && (apiData.trades != null || apiData.period || apiData.per_asset)) {
      return {
        ...apiData,
        certificate_id: apiData.certificate_id ?? certificateId,
      };
    }
    return {
      ...SAMPLE_REVIEW,
      certificate_id: certificateId ?? SAMPLE_REVIEW.certificate_id,
    };
  }, [apiData, certificateId, item]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open || !item) return null;

  const dateRange = formatDateRange(review.period?.from, review.period?.to);
  const longPercent = review.long_short?.long_percent ?? 50;
  const shortPercent = review.long_short?.short_percent ?? 50;

  const maxDistributionCount = Math.max(
    ...(review.pnl_distribution ?? []).map((d) => d.count),
    1
  );

  return (
    <div className="prm-overlay" onClick={onClose}>
      <div className="prm-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <header className="prm-header">
          <div className="prm-header__left">
            <h2 className="prm-header__title">{t('Payout review')}</h2>
            {loading ? (
              <Skeleton w="115px" h={14} radius={4} />
            ) : (
              dateRange && <span className="prm-header__date">{dateRange}</span>
            )}
          </div>

          <div className="prm-header__actions">
            <button
              type="button"
              className={`prm-action-btn prm-share-btn ${item?.public ? 'is-public' : ''}`}
              onClick={handleShareClick}
              title={item?.public ? t('Shared — anyone with the link can view it') : t('Share this certificate')}
            >
              <GlobeIcon />
              <span>{item?.public ? t('Shared') : t('Share')}</span>
            </button>

            <a
              href="https://fundedaward.com/products/ac-award?ta_aff=capital"
              target="_blank"
              rel="noreferrer"
              className="prm-action-btn prm-frame-btn"
              title={t('Frame certificate')}
            >
              <FrameIcon />
              <span>
                {t('Frame')}<span className="prm-frame-btn__long"> {t('Certificate')}</span>
              </span>
            </a>

            <button
              type="button"
              className="prm-close-btn"
              onClick={onClose}
              aria-label={t('Close modal')}
            >
              <svg
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              >
                <path d="M2.5 2.5l9 9M11.5 2.5l-9 9" />
              </svg>
            </button>
          </div>
        </header>

        {/* Content Body: Skeleton vs Loaded */}
        {loading ? (
          <PayoutReviewSkeleton item={item} />
        ) : (
          <div className="prm-body">
            {/* Left Column */}
            <div className="prm-col-left">
              <div className="prm-cert-preview">
                <CertificateArt item={item} />
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadRenderedCertificate(item);
                }}
                className="prm-download-btn"
              >
                {t('Download certificate')}
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M8 2.5v8M5 7.5l3 3 3-3M3 13.5h10" />
                </svg>
              </button>

              <div className="prm-summary-stats">
                <div className="prm-stat-box">
                  <span className="prm-stat-box__label">{t('Trades')}</span>
                  <span className="prm-stat-box__value">{review.trades ?? 0}</span>
                </div>

                <div className="prm-stat-box">
                  <span className="prm-stat-box__label">{t('Win rate')}</span>
                  <span className="prm-stat-box__value">{review.win_rate ?? 0}%</span>
                  <div className="prm-stat-box__breakdown">
                    <span className="prm-stat-box__win">{t('{{count}}W', { count: review.wins ?? 0 })}</span>
                    <span className="prm-stat-box__slash">/</span>
                    <span className="prm-stat-box__loss">{t('{{count}}L', { count: review.losses ?? 0 })}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="prm-col-right">
              {/* Per-asset performance + Certificate ID */}
              <div className="prm-asset-head">
                <span className="prm-asset-head__title">{t('Per-asset performance')}</span>
                <span className="prm-asset-head__cert-id">
                  {t('Certificate@{{id}}', { id: review.certificate_id })}
                </span>
              </div>

              {/* Long / Short Bar */}
              <div className="prm-ratio-section">
                <span className="prm-ratio-label">{t('Long / short')}</span>
                <div className="prm-ratio-bar">
                  <div
                    className="prm-ratio-bar__long"
                    style={{ width: `${longPercent}%` }}
                  />
                  <div
                    className="prm-ratio-bar__short"
                    style={{ width: `${shortPercent}%` }}
                  />
                </div>
                <span className="prm-ratio-sub">
                  {t('{{long}}% long · {{short}}% short', { long: longPercent, short: shortPercent })}
                </span>
              </div>

              {/* Assets Table */}
              <div className="prm-table-wrapper">
                <table className="prm-table">
                  <thead>
                    <tr>
                      <th className="prm-th prm-th--left">{t('ASSET')}</th>
                      <th className="prm-th prm-th--center">{t('VOLUME')}</th>
                      <th className="prm-th prm-th--center">{t('TRADES')}</th>
                      <th className="prm-th prm-th--center">{t('WIN%')}</th>
                      <th className="prm-th prm-th--right">{t('NET PNL')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(review.per_asset ?? []).map((row, idx) => (
                      <tr key={row.asset ?? idx} className="prm-tr">
                        <td className="prm-td prm-td--asset">{cleanAsset(row.asset)}</td>
                        <td className="prm-td prm-td--center">{formatVolume(row.volume)}</td>
                        <td className="prm-td prm-td--center">{row.trades ?? '—'}</td>
                        <td className="prm-td prm-td--center">{row.win_rate ?? 0}%</td>
                        <td
                          className={[
                            'prm-td',
                            'prm-td--right',
                            'prm-td--pnl',
                            row.net_pnl > 0 && 'is-profit',
                            row.net_pnl < 0 && 'is-loss',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {row.net_pnl === 0
                            ? '$0.00'
                            : signedUsd(row.net_pnl, { decimals: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Best Trade & Worst Trade Cards */}
              <div className="prm-trades-grid">
                {review.best_trade && (
                  <div className="prm-trade-card is-best">
                    <span className="prm-trade-card__tag">{t('BEST TRADE')}</span>
                    <span className="prm-trade-card__sub">
                      {cleanAsset(review.best_trade.symbol)} ·{' '}
                      {t(String(review.best_trade.side ?? '').toLowerCase())}
                    </span>
                    <span className="prm-trade-card__val is-profit">
                      +{usd(review.best_trade.net_profit, { decimals: 2 })}
                    </span>
                  </div>
                )}

                {review.worst_trade && (
                  <div className="prm-trade-card is-worst">
                    <span className="prm-trade-card__tag">{t('WORST TRADE')}</span>
                    <span className="prm-trade-card__sub">
                      {cleanAsset(review.worst_trade.symbol)} ·{' '}
                      {t(String(review.worst_trade.side ?? '').toLowerCase())}
                    </span>
                    <span className="prm-trade-card__val is-loss">
                      -{usd(Math.abs(review.worst_trade.net_profit), { decimals: 2 })}
                    </span>
                  </div>
                )}
              </div>

              {/* PnL Distribution Chart */}
              {review.pnl_distribution?.length > 0 && (
                <div className="prm-distribution">
                  <span className="prm-distribution__title">{t('PnL distribution')}</span>
                  <div className="prm-chart-container">
                    {review.pnl_distribution.map((bucket, i) => {
                      const heightPercent =
                        bucket.count > 0
                          ? Math.max(Math.round((bucket.count / maxDistributionCount) * 100), 18)
                          : 4;

                      const isLoss = bucket.type === 'loss';
                      const signFrom = bucket.from > 0 ? '+' : '';
                      const signTo = bucket.to > 0 ? '+' : '';
                      const range = {
                        count: bucket.count,
                        from: `${signFrom}$${bucket.from.toFixed(2)}`,
                        to: `${signTo}$${bucket.to.toFixed(2)}`,
                      };
                      const tooltip =
                        bucket.count === 1
                          ? t('{{count}} trade ({{from}} to {{to}})', range)
                          : t('{{count}} trades ({{from}} to {{to}})', range);

                      return (
                        <div
                          key={i}
                          className="prm-chart-bar-slot"
                          title={tooltip}
                        >
                          <div
                            className={`prm-chart-bar ${isLoss ? 'is-loss' : 'is-win'}`}
                            style={{ height: `${heightPercent}%` }}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <div className="prm-chart-axis">
                    <span className="prm-chart-axis__label">{t('losses')}</span>
                    <span className="prm-chart-axis__label">{t('wins')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Skeleton layout precisely matching the loaded modal geometry */
function PayoutReviewSkeleton({ item }) {
  return (
    <div className="prm-body">
      {/* Left Column Skeleton */}
      <div className="prm-col-left">
        <div className="prm-cert-preview">
          <CertificateArt item={item} />
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            downloadRenderedCertificate(item);
          }}
          className="prm-download-btn"
        >
          {t('Download certificate')}
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M8 2.5v8M5 7.5l3 3 3-3M3 13.5h10" />
          </svg>
        </button>

        <div className="prm-summary-stats">
          <div className="prm-stat-box">
            <Skeleton w="48px" h={13} radius={3} />
            <div style={{ marginTop: 6 }}>
              <Skeleton w="48px" h={28} radius={4} />
            </div>
          </div>

          <div className="prm-stat-box">
            <Skeleton w="56px" h={13} radius={3} />
            <div style={{ marginTop: 6 }}>
              <Skeleton w="62px" h={28} radius={4} />
            </div>
            <div style={{ marginTop: 6 }}>
              <Skeleton w="72px" h={13} radius={3} />
            </div>
          </div>
        </div>
      </div>

      {/* Right Column Skeleton */}
      <div className="prm-col-right">
        {/* Top title and cert ID */}
        <div className="prm-asset-head">
          <span className="prm-asset-head__title">{t('Per-asset performance')}</span>
          <Skeleton w="110px" h={14} radius={4} />
        </div>

        {/* Long / Short Bar */}
        <div className="prm-ratio-section">
          <Skeleton w="68px" h={12} radius={3} />
          <Skeleton w="100%" h={8} pill />
          <Skeleton w="130px" h={12} radius={3} />
        </div>

        {/* Assets Table Skeleton */}
        <div className="prm-table-wrapper">
          <table className="prm-table">
            <thead>
              <tr>
                <th className="prm-th prm-th--left">{t('ASSET')}</th>
                <th className="prm-th prm-th--center">{t('VOLUME')}</th>
                <th className="prm-th prm-th--center">{t('TRADES')}</th>
                <th className="prm-th prm-th--center">{t('WIN%')}</th>
                <th className="prm-th prm-th--right">{t('NET PNL')}</th>
              </tr>
            </thead>
            <tbody>
              {[
                { w1: 58, w2: 36, w3: 20, w4: 32, w5: 68 },
                { w1: 64, w2: 38, w3: 20, w4: 28, w5: 58 },
                { w1: 60, w2: 32, w3: 20, w4: 28, w5: 62 },
                { w1: 66, w2: 42, w3: 22, w4: 30, w5: 70 },
              ].map((row, idx) => (
                <tr key={idx} className="prm-tr">
                  <td className="prm-td prm-td--asset">
                    <Skeleton w={`${row.w1}px`} h={13} radius={3} />
                  </td>
                  <td className="prm-td prm-td--center">
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Skeleton w={`${row.w2}px`} h={13} radius={3} />
                    </div>
                  </td>
                  <td className="prm-td prm-td--center">
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Skeleton w={`${row.w3}px`} h={13} radius={3} />
                    </div>
                  </td>
                  <td className="prm-td prm-td--center">
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Skeleton w={`${row.w4}px`} h={13} radius={3} />
                    </div>
                  </td>
                  <td className="prm-td prm-td--right">
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Skeleton w={`${row.w5}px`} h={13} radius={3} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Trade Cards Skeleton */}
        <div className="prm-trades-grid">
          <div className="prm-trade-card prm-trade-card--skeleton">
            <Skeleton w="70px" h={11} radius={3} />
            <Skeleton w="110px" h={15} radius={4} />
            <Skeleton w="95px" h={22} radius={4} />
          </div>

          <div className="prm-trade-card prm-trade-card--skeleton">
            <Skeleton w="76px" h={11} radius={3} />
            <Skeleton w="110px" h={15} radius={4} />
            <Skeleton w="95px" h={22} radius={4} />
          </div>
        </div>

        {/* PnL Distribution Skeleton */}
        <div className="prm-distribution">
          <span className="prm-distribution__title">{t('PnL distribution')}</span>
          <div className="prm-chart-container">
            {[45, 38, 52, 28, 62, 88, 74, 42, 78, 56].map((h, i) => (
              <div key={i} className="prm-chart-bar-slot">
                <Skeleton w="100%" h={`${h}%`} radius="4px 4px 0 0" />
              </div>
            ))}
          </div>
          <div className="prm-chart-axis">
            <span className="prm-chart-axis__label">{t('losses')}</span>
            <span className="prm-chart-axis__label">{t('wins')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="5.25" />
      <path d="M1.75 7h10.5" />
      <path d="M7 1.75c1.4 1.5 2.1 3.3 2.1 5.25S8.4 10.75 7 12.25C5.6 10.75 4.9 8.95 4.9 7s.7-3.75 2.1-5.25z" />
    </svg>
  );
}

function FrameIcon() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.75" y="1.75" width="10.5" height="10.5" rx="2" />
      <rect x="4" y="4" width="6" height="6" rx="1" />
    </svg>
  );
}
